// features/projects/templates/professional/widgets/simulator-snapshot.js
// ═══════════════════════════════════════════════════════════
// SIMULATOR SNAPSHOT — Tier-A widget
//
// Q: "How much research material exists in this project right
//     now?" Counts of cases, hypotheses, predictions, questions —
//     a quick-read inventory for the dashboard's bottom row.
//
// Reference layout shows 2 large numbers + secondary stats
// stacked. This adapts to the Continuum data model: substantive
// findings count + median Phase A status.
// ═══════════════════════════════════════════════════════════

import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { WidgetShell } from './findings-status';

export default function SimulatorSnapshotWidget() {
  const { loading, comparisons, hypotheses, predictions, questions } = useWidgetData();

  if (loading) {
    return <WidgetShell label="Simulator Snapshot"><Loading /></WidgetShell>;
  }

  const substantive = (comparisons || []).filter(c =>
    c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase())
  );
  const phaseA = substantive.filter(c => c.phase_a_status).length;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = (predictions || []).filter(p =>
    p.status === 'pending' && new Date(p.resolution_date) <= today
  ).length;

  return (
    <WidgetShell label="Simulator Snapshot">
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24,
        marginBottom: 14,
      }}>
        <BigStat
          value={substantive.length}
          label="substantive findings"
          sub={`${phaseA} Phase A complete`}
        />
        <BigStat
          value={(predictions || []).length}
          label="predictions registered"
          sub={due > 0 ? `${due} due now` : `${(predictions || []).filter(p => p.status === 'pending').length} pending`}
          subColor={due > 0 ? L.copper : L.dim}
        />
      </div>
      <div style={{
        paddingTop: 12, borderTop: `1px dashed ${L.b1}`,
        display: 'flex', flexWrap: 'wrap', gap: 16,
        fontFamily: FONT.mono, fontSize: 10, color: L.dim,
        letterSpacing: '0.06em',
      }}>
        <Inline value={(hypotheses || []).length} label="hypotheses" />
        <Inline value={(questions || []).length} label="open questions" />
        <Inline value={(comparisons || []).length} label="dynamics tracked" />
      </div>
    </WidgetShell>
  );
}

function BigStat({ value, label, sub, subColor }) {
  return (
    <div>
      <div style={{
        fontFamily: FONT.serif, fontSize: 36, fontWeight: 400,
        color: L.text, lineHeight: 1, letterSpacing: '-0.01em',
      }}>
        {value}
      </div>
      <div style={{
        fontFamily: FONT.sans, fontSize: 11, color: L.mid,
        marginTop: 6, lineHeight: 1.45,
      }}>
        {label}
      </div>
      {sub && (
        <div style={{
          fontFamily: FONT.mono, fontSize: 9, color: subColor || L.dim,
          marginTop: 4, letterSpacing: '0.06em',
        }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Inline({ value, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
      <span style={{ color: L.text, fontWeight: 600 }}>{value}</span>
      <span>{label}</span>
    </span>
  );
}

function Loading() {
  return <div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div>;
}
